import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { intersection } from 'lodash-es'
import { nonNull } from 'commons-mathrail/shared/fns'
import { currentLocale } from '../i18n'
import { toOptions } from '../shared/fns'
import { currentUser, requestedPathname, setHasLoggedIn, token } from '../shared/local-storage'
import { docsUrlPrefix } from '../shared/system-state'
import { clearAuthentication, setAuthentication } from '../api-config'
import { useMyUserLazy } from './user-api'
import { setDefaultModule } from '../module/module-logic'
import { deleteSession } from '../session/session-api'
import { flushOpsLogQueue } from '../ops-log/ops-log-api'
import { isDemo } from '../demo-course-lesson/demo-course-lesson-state'
import { systemLoading } from '../components/system-loading-state'

export const getCurrentUserFullName = () =>
  currentUser.value && `${currentUser.value.firstName} ${currentUser.value.lastName}`

export const hasRole = (...roles) => intersection(currentUser.value?.roles, roles).length > 0

export const isRoot = computed(() => hasRole('root'))

const roles = [
  isRoot.value ? 'root' : null,
  'admin', 'engineer', 'instructor', 'student'
].filter(nonNull)
export const roleOptions = toOptions(roles)

export const hasModule = (...modules) => intersection(currentUser.value?.modules, modules).length

const passwordChars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

export const generatePassword = () =>
  [...Array(8)]
    .map(() => passwordChars.charAt(Math.floor(Math.random() * passwordChars.length)))
    .join('')

export const useReloadMyUser = () => {
  const { data: myUser, refetch: reloadMyUser } = useMyUserLazy()
  return async () => {
    await reloadMyUser()
    currentUser.value = myUser.value
  }
}

export const useVerifyLoggedIn = () => {
  const reloadMyUser = useReloadMyUser()
  return async () => {
    if (!currentUser.value) {
      await reloadMyUser()
    }
  }
}

export const useOnLoggedIn = (defaultRoute = null) => {
  const router = useRouter()
  const { data: myUser } = useMyUserLazy()
  const reloadMyUser = useReloadMyUser()

  return async newToken => {
    token.value = newToken
    setAuthentication()
    setHasLoggedIn()

    await reloadMyUser()

    setDefaultModule()

    if (myUser.value?.shouldChangePassword) {
      await router.push({ name: 'UserSettings' })
    } else if (requestedPathname.value) {
      await router.push(requestedPathname.value)
      requestedPathname.value = null
    } else if (defaultRoute) {
      await router.push(defaultRoute)
    }
  }
}

export const useLogout = () => {
  const router = useRouter()
  return async () => {
    systemLoading.value = true
    try {
      await flushOpsLogQueue()
    } catch (error) {} // ignore
    try {
      await deleteSession()
    } catch (error) {} // ignore
    token.value = null
    currentUser.value = null
    clearAuthentication()
    await router.push({ name: isDemo.value ? 'DemoLogin' : 'Login' })
    window.location.reload() // reset data to prevent leaking into next user session
  }
}

export const emailOrUsernameRequired = val => schema => schema.required(val('emailOrUsernameRequired'))
export const isStudent = roles => JSON.stringify(roles) === '["student"]'
export const usernameRegexp = /^[a-zA-Z0-9_]*$/

const privacyPolicyLang = computed(() => currentLocale.value === 'cs' ? 'cs' : 'en')
export const privacyPolicyUrl = computed(() =>
  `${docsUrlPrefix.value}/demo/mathrail-privacy-policy-${privacyPolicyLang.value}.pdf`
)
